<template>
  <div
    v-if="!modelValue"
    class="flex items-center justify-center w-full text-[var(--el-text-color-placeholder)] italic py-5 text-center bg-[#fafbfc] rounded-md border border-dashed border-[var(--el-border-color-light)]"
  >
    No hay procesos configurados para el elemento&nbsp;<span class="font-bold">
      {{ elementoTipo.Descripcion }}</span
    >.
  </div>
  <el-collapse
    v-else
    v-model="collapseModel"
    :accordion="false"
  >
    <input-proceso-seccion
      v-for="(seccion, indexSeccion) in modelValue"
      :key="`input_procesos_${indexSeccion}`"
      :model-value="seccion"
      :elemento-tipo="elementoTipo.Codigo"
      :codigo-familia-material="CodigoFamiliaMaterial"
      :validar-porcentaje="validarPorcentaje && seccion.Codigo === Seccion.MATERIAS_PRIMAS"
      :procesos-del-elemento="procesosDelElemento"
      @update:model-value="handleUpdateModelValue(indexSeccion, $event)"
    />
  </el-collapse>

  <!-- COTI-642: FAB para ver el diagrama de flujo de procesos. -->
  <!-- <button
    v-if="procesosDelElemento.length > 1"
    type="button"
    class="fab-diagrama-flujo"
    title="Ver diagrama de flujo de procesos"
    @click="abrirDiagramaFlujo"
  >
    <IconSitemap class="fab-icono" />
  </button> -->

  <procesos-flujo-modal
    v-model:visible="mostrarDiagramaFlujo"
    :procesos="procesosDelElemento"
  />
</template>

<script setup>
import {
  onMounted,
  ref,
  inject,
  provide,
  computed,
  watch,
} from 'vue';
import InputProcesoSeccion from '@/shared/components/procesos/input_procesos_seccion.vue';
import ProcesosFlujoModal from '@/shared/components/procesos/procesos_flujo_modal.vue';
import IconSitemap from '~icons/mdi/sitemap-outline';
import { ElementoBase } from '@/enum/elemento_base';
import { Seccion } from '@/enum/seccion';
import {
  aplanarSecciones,
  aplanarProcesosParaPrecedentes,
  asegurarUidsProcesos,
  asegurarProcesoInicial,
  materialesOrigenDesdeItemProceso,
  podarProcesoOrigenInvalido,
} from '@/shared/utils/procesos_precedentes';

const currentElementoBase = inject('currentElementoBase');
const collapseModel = ref([]);
// COTI-642: modal del diagrama de flujo.
const mostrarDiagramaFlujo = ref(false);
const abrirDiagramaFlujo = () => {
  mostrarDiagramaFlujo.value = true;
};

const props = defineProps({
  modelValue: {
    type: Array,
    required: true,
  },
  elementoTipo: {
    type: Object,
    required: true,
  },
  CodigoFamiliaMaterial: {
    type: Number,
    required: false,
  },
});

const emit = defineEmits(['update:modelValue']);

// ── COTI-642: proceso origen del material ─────────────────────────────────────
// Asigna `_uid` a cada proceso de forma imperativa (side-effect fuera del computed), de
// inmediato durante el setup para que exista antes de que rendericen los hijos.
watch(
  () => props.modelValue,
  () => asegurarUidsProcesos(aplanarSecciones(props.modelValue || [])),
  { deep: true, immediate: true },
);

// Lista aplanada (todas las secciones, orden global) para el selector "Proceso" de la grilla
// de materiales y el diagrama de flujo. Computed PURO: sólo lee.
const procesosDelElemento = computed(() =>
  aplanarProcesosParaPrecedentes(aplanarSecciones(props.modelValue || [])),
);
provide('procesosDelElemento', procesosDelElemento);

/**
 * Poda las referencias `ProcesoOrigenUid` inválidas (proceso borrado/reordenado) y garantiza
 * que exista un proceso inicial por defecto (el de menor orden si nadie lo marcó).
 */
const reconciliarOrigenMateriales = () => {
  const planos = aplanarSecciones(props.modelValue || []);
  let cambio = podarProcesoOrigenInvalido(planos);
  cambio = asegurarProcesoInicial(planos) || cambio;
  if (cambio) {
    emit(
      'update:modelValue',
      (props.modelValue || []).map((seccion) => ({ ...seccion })),
    );
  }
};

onMounted(() => {
  collapseModel.value = (props.modelValue || [])
    .filter((s) => s.ElementoProceso.length > 0)
    .map((s) => s.Codigo);

  // Hidratación: convierte ItemProcesoOrigen (posiciones del backend) a ProcesoOrigenUid.
  materialesOrigenDesdeItemProceso(aplanarSecciones(props.modelValue || []));
  reconciliarOrigenMateriales();
});

// Al agregar / eliminar / reordenar procesos, poda referencias que quedaron inválidas.
watch(
  () => props.modelValue,
  () => reconciliarOrigenMateriales(),
  { deep: true },
);
// ─────────────────────────────────────────────────────────────────────────────

const handleUpdateModelValue = (index, updatedSeccion) => {
  const updatedModelValue = props.modelValue.map((seccion, idx) => {
    if (idx === index) {
      return { ...updatedSeccion };
    }
    return seccion;
  });

  emit('update:modelValue', updatedModelValue);
};

const validarPorcentaje = computed(() => {
  // TODO: deberia agregar condicion por modulo Cotizacion?
  return currentElementoBase === ElementoBase.EXTRUSION;
});
</script>

<style scoped>
/* Alineado con .floating-toggle-btn del panel de info de producción: misma columna
   (right/bottom + size), apilado justo encima cuando ambos están presentes. */
.fab-diagrama-flujo {
  position: fixed;
  right: 32px;
  bottom: 100px;
  z-index: 1999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: #409eff;
  color: #fff;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(64, 158, 255, 0.3);
  transition:
    transform 0.12s ease,
    filter 0.12s ease;
}
.fab-diagrama-flujo:hover {
  filter: brightness(1.08);
  transform: translateY(-1px);
}
.fab-diagrama-flujo:active {
  transform: translateY(0);
}
.fab-icono {
  width: 20px;
  height: 20px;
}
</style>
